/**
 * Compliance Documents — Phase 1
 * -------------------------------
 * Sits inside /app/documentation as a new tile.
 *
 *  Left pane  : folder tree (arbitrary depth). Click a folder to
 *               scope the right pane; long-press / context menu
 *               to rename / delete / add sub-folder.
 *  Right pane : documents in the current folder (or a global
 *               master-list view). Search, upload, download, edit
 *               metadata, mark obsolete, XLSX export.
 *
 *  After upload: if the file's doc_no matches PARENT-(F##), the API
 *  returns a `suggested_parent`. We surface it as an inline confirm
 *  banner asking the user to link them — never auto-linked.
 */
import { createElement, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import api, { formatErr } from "@/lib/api";
import { Portal } from "@/components/Portal";
import { toast } from "sonner";
import {
  FolderPlus, Folder, FolderOpen, FileText, Plus, Upload,
  Search, Download, Trash2, X, ChevronRight, ChevronDown, Edit3,
  Link2, RefreshCw, Grid3x3, ListTree, EyeOff, Eye, ArrowUpDown,
  GitBranch, ClipboardList, CheckCircle2, XCircle, RotateCcw, Clock, History,
  MoreVertical, AlertOctagon,
} from "lucide-react";

// Filename-parser: extracts { doc_no, revision, title, doc_type } from
// filenames like "SPG-P-062-(F01)-00 Management Programme for No2.pdf"
//                "JIFP-M-01-02 Food Safety Manual.docx"
//                "SPG-PRP-04-(F02) Cleaning Log.xlsx"
// We split the filename (sans extension) into a token part and a title
// part. The token part is a hyphen-separated pattern like `PREFIX-{M|P|PRP|WI|SOP}-N…`
// optionally followed by `(F##)` and/or a trailing `-RR` revision.
function parseComplianceFilename(name) {
  if (!name) return {};
  // strip extension
  const base = name.replace(/\.[^.]+$/, "").trim();
  // Try the strict pattern first: <token> <space> <title>
  const m = base.match(/^([A-Z0-9][A-Z0-9\-()]*[A-Z0-9)])[\s_]+(.+)$/i);
  let token = "", title = "";
  if (m) {
    token = m[1].trim();
    title = m[2].trim();
  } else {
    return { title: base };
  }
  // Detect trailing revision — STRICTLY 2 digits only. Anything 3-digit
  // (or 3+ digit) stays part of the document number. This matches the
  // house convention: "PREFIX-P-062" (3-digit doc no) + optional "-01"
  // (2-digit revision). So "SPG-P-062-01" → doc_no=SPG-P-062, rev=01;
  // but "SPG-P-062" alone → doc_no=SPG-P-062, rev="".
  let rev = "";
  const revMatch = token.match(/-([0-9]{2})$/);
  if (revMatch) {
    rev = revMatch[1];
    token = token.slice(0, -revMatch[0].length);
  }
  // Detect (F##) suffix → form
  const hasForm = /\(F\d{1,3}\)/i.test(token);
  // Detect the doc-type from tokens (order matters — most specific first)
  let docType = "procedure";
  if (hasForm) docType = "form";
  else if (/\-PRP\-/i.test(token) || /\-PRP$/i.test(token)) docType = "prp";
  else if (/\-SOP\-/i.test(token) || /\-SOP$/i.test(token)) docType = "sop";
  else if (/\-WI\-/i.test(token) || /\-WI$/i.test(token)) docType = "wi";
  else if (/\-M\-/i.test(token) || /\-M$/i.test(token)) docType = "manual";
  else if (/\-P\-/i.test(token)) docType = "procedure";
  return {
    doc_no: token,
    revision: rev,
    title,
    doc_type: docType,
  };
}

const DOC_TYPES = [
  ["manual",    "Manual"],
  ["procedure", "Procedure"],
  ["prp",       "PRP (Prerequisite Program)"],
  ["wi",        "WI (Work Instruction)"],
  ["sop",       "SOP"],
  ["form",      "Form"],
  ["appendix",  "Appendix"],
  ["other",     "Other"],
];

const STATUS_LABEL = {
  active: "Active",
  under_review: "Under review",
  obsolete: "Obsolete",
};

const fmt = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined,
      { day: "2-digit", month: "short", year: "numeric" });
  } catch { return v; }
};

/* ============ FOLDER TREE HELPERS ============ */
function toTree(rows) {
  const map = {};
  const roots = [];
  rows.forEach((r) => { map[r.id] = { ...r, children: [] }; });
  rows.forEach((r) => {
    const node = map[r.id];
    if (r.parent_id && map[r.parent_id]) map[r.parent_id].children.push(node);
    else roots.push(node);
  });
  return roots;
}

function FolderNode({ node, depth, activeId, expanded, onToggle, onSelect,
                      onAddChild, onRename, onDelete, canWrite }) {
  const isOpen = expanded.has(node.id);
  const hasKids = node.children.length > 0;
  return (
    <li>
      <div className={`flex items-center gap-1 py-1 px-1.5 rounded cursor-pointer text-sm ${
          activeId === node.id ? "bg-[var(--vb-elev)]" : "hover:bg-[var(--vb-elev)]"}`}
        style={{ paddingLeft: `${depth * 12 + 6}px` }}
        onClick={() => onSelect(node.id)}
        data-testid={`cd-folder-${node.id}`}>
        <button className="opacity-70 hover:opacity-100"
          onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
          style={{ padding: "1px 2px", visibility: hasKids ? "visible" : "hidden" }}
          data-testid={`cd-folder-toggle-${node.id}`}>
          {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </button>
        {isOpen && hasKids ? <FolderOpen size={14} /> : <Folder size={14} />}
        <span className="flex-1 truncate">{node.name}</span>
        <span className="text-[10px] opacity-50">{node.doc_count ?? 0}</span>
        {canWrite && activeId === node.id && (
          <div className="flex gap-0.5 ml-1">
            <button className="opacity-60 hover:opacity-100"
              title="Add sub-folder"
              onClick={(e) => { e.stopPropagation(); onAddChild(node.id); }}
              data-testid={`cd-folder-add-child-${node.id}`}>
              <FolderPlus size={12} />
            </button>
            <button className="opacity-60 hover:opacity-100" title="Rename"
              onClick={(e) => { e.stopPropagation(); onRename(node); }}
              data-testid={`cd-folder-rename-${node.id}`}>
              <Edit3 size={12} />
            </button>
            <button className="opacity-60 hover:opacity-100" title="Delete"
              onClick={(e) => { e.stopPropagation(); onDelete(node); }}
              data-testid={`cd-folder-delete-${node.id}`}>
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </div>
      {isOpen && hasKids && (
        <ul className="list-none pl-0 m-0">
          {node.children.map((c) =>
            createElement(FolderNode, {
              key: c.id, node: c, depth: depth + 1,
              activeId, expanded, onToggle, onSelect,
              onAddChild, onRename, onDelete, canWrite,
            })
          )}
        </ul>
      )}
    </li>
  );
}

/* ============ MAIN SECTION ============ */
export default function ComplianceDocsSection() {
  const { user } = useAuth();
  const { confirm, notify } = useDialog();
  const canWrite = ["owner", "manager"].includes(user?.role);
  const isOwner = user?.role === "owner";

  const [folders, setFolders] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [activeFolderId, setActiveFolderId] = useState(null);   // null = master-list
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");     // all | active | under_review
  const [showObsolete, setShowObsolete] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [revisingDoc, setRevisingDoc] = useState(null);   // active-doc → open request modal
  const [showQueue, setShowQueue] = useState(false);
  const [pendingRevs, setPendingRevs] = useState([]);
  const [expandedDocs, setExpandedDocs] = useState({});  // { docId: [historyRows] | 'loading' }
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  const loadFolders = useCallback(async () => {
    const { data } = await api.get("/compliance-docs/folders/tree");
    setFolders(data.folders || []);
  }, []);
  const loadSummary = useCallback(async () => {
    const { data } = await api.get("/compliance-docs/summary");
    setSummary(data);
  }, []);
  const loadDocs = useCallback(async () => {
    const params = new URLSearchParams();
    if (activeFolderId) params.set("folder_id", activeFolderId);
    if (q) params.set("q", q);
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (showObsolete) params.set("include_obsolete", "true");
    const { data } = await api.get(`/compliance-docs/documents?${params.toString()}`);
    setDocs(data.documents || []);
  }, [activeFolderId, q, statusFilter, showObsolete]);
  const loadRevisions = useCallback(async () => {
    const { data } = await api.get("/compliance-docs/revision-requests");
    setPendingRevs(data.requests || []);
  }, []);

  useEffect(() => { loadFolders(); loadSummary(); loadRevisions(); },
    [loadFolders, loadSummary, loadRevisions]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const tree = useMemo(() => toTree(folders), [folders]);
  const folderById = useMemo(() => {
    const m = {}; folders.forEach((f) => { m[f.id] = f; }); return m;
  }, [folders]);
  const breadcrumb = useMemo(() => {
    if (!activeFolderId) return [];
    const chain = [];
    let cur = folderById[activeFolderId];
    while (cur) { chain.unshift(cur); cur = cur.parent_id ? folderById[cur.parent_id] : null; }
    return chain;
  }, [activeFolderId, folderById]);

  const seedDefaults = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/compliance-docs/folders/seed-defaults");
      toast.success(`Created ${data.created?.length || 0} default folder(s)`);
      await loadFolders();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Seed failed");
    } finally { setBusy(false); }
  };
  const toggleFolder = (id) => setExpanded((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const [folderPrompt, setFolderPrompt] = useState(null);
  // { mode: 'create' | 'rename', parentId?, initialName, onSave }

  const openCreateFolder = (parentId) => setFolderPrompt({
    mode: "create",
    parentId: parentId || null,
    initialName: "",
    onSave: async (name) => {
      await api.post("/compliance-docs/folders",
        { name, parent_id: parentId || null });
      toast.success("Folder created");
      setFolderPrompt(null);
      await loadFolders();
      if (parentId) setExpanded((s) => new Set(s).add(parentId));
    },
  });
  const openRenameFolder = (node) => setFolderPrompt({
    mode: "rename",
    initialName: node.name,
    onSave: async (name) => {
      await api.put(`/compliance-docs/folders/${node.id}`, { name });
      toast.success("Renamed");
      setFolderPrompt(null);
      await loadFolders();
    },
  });
  const deleteFolder = async (node) => {
    if (!await confirm({
      title: `Delete "${node.name}"?`,
      message: "Only empty folders can be deleted.",
      danger: true, confirmLabel: "Delete",
    })) return;
    try {
      await api.delete(`/compliance-docs/folders/${node.id}`);
      toast.success("Deleted");
      if (activeFolderId === node.id) setActiveFolderId(null);
      await loadFolders();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/compliance-docs/documents/${d.id}/download`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.file_name || "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Download failed");
    }
  };
  const obsoleteDoc = async (d) => {
    if (!await confirm({
      title: `Mark "${d.title}" obsolete?`,
      message: "The document stays in the master list (visible with the 'Show obsolete' toggle) for audit retention.",
      confirmLabel: "Mark obsolete",
    })) return;
    try {
      await api.delete(`/compliance-docs/documents/${d.id}`);
      toast.success("Marked obsolete");
      await Promise.all([loadDocs(), loadSummary()]);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed");
    }
  };
  const hardDeleteDoc = async (d) => {
    const isActive = d.status === "active";
    if (!await confirm({
      title: `Permanently delete "${d.title}"?`,
      message: isActive
        ? `⚠️ This is the CURRENT ACTIVE revision. Permanent deletion removes the document and its file from the archive completely. Only do this for accidental uploads or mistakes — audit history for this document will be lost. This cannot be undone.`
        : `This will remove the document and its file completely from the archive. Use this only for mistakes (wrong file uploaded, duplicate, test data). This cannot be undone.`,
      danger: true, confirmLabel: "Delete permanently",
    })) return;
    try {
      await api.delete(`/compliance-docs/documents/${d.id}?hard=true`);
      toast.success("Permanently deleted");
      await Promise.all([loadDocs(), loadSummary()]);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    }
  };
  const toggleHistory = async (d) => {
    const cur = expandedDocs[d.id];
    if (cur && cur !== "loading") {
      setExpandedDocs((s) => { const n = { ...s }; delete n[d.id]; return n; });
      return;
    }
    setExpandedDocs((s) => ({ ...s, [d.id]: "loading" }));
    try {
      const { data } = await api.get(`/compliance-docs/documents/${d.id}/history`);
      setExpandedDocs((s) => ({ ...s, [d.id]: data.history || [] }));
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "History load failed");
      setExpandedDocs((s) => { const n = { ...s }; delete n[d.id]; return n; });
    }
  };
  const exportXLSX = async () => {
    try {
      const res = await api.get(
        `/compliance-docs/documents/master-list.xlsx?include_obsolete=${showObsolete}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Document_Control_Master_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Master list exported");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Export failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="compliance-docs-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="vb-heading text-2xl m-0 flex items-center gap-2">
            <FolderOpen size={20} /> Compliance Documents
          </h2>
          <p className="text-xs opacity-70 mt-0.5">
            {summary && (
              <>
                {summary.documents.total} document
                {summary.documents.total !== 1 ? "s" : ""} · {summary.documents.active} active
                {summary.documents.obsolete ? ` · ${summary.documents.obsolete} obsolete` : ""}
                {" · "}{summary.folders} folder{summary.folders !== 1 ? "s" : ""}
              </>
            )}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="vb-btn ghost sm" onClick={() => { loadFolders(); loadDocs(); loadSummary(); loadRevisions(); }}
            data-testid="cd-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
          <button className="vb-btn ghost sm" onClick={() => setShowQueue(true)}
            data-testid="cd-open-queue">
            <ClipboardList size={13} /> Revision queue
            {(summary?.pending_revisions ?? 0) > 0 && (
              <span className="vb-pill warn text-[10px] ml-1"
                data-testid="cd-pending-badge">
                {summary.pending_revisions}
              </span>
            )}
          </button>
          <button className="vb-btn ghost sm" onClick={exportXLSX}
            data-testid="cd-export-xlsx">
            <Download size={13} /> Export XLSX
          </button>
          {canWrite && (
            <button className="vb-btn sm" onClick={() => setShowUpload(true)}
              disabled={folders.length === 0}
              title={folders.length === 0 ? "Create a folder first" : "Upload a document"}
              data-testid="cd-upload">
              <Upload size={13} /> Upload document
            </button>
          )}
        </div>
      </div>

      {/* Empty-state seed */}
      {folders.length === 0 && (
        <div className="vb-card p-6 text-center" data-testid="cd-empty">
          <FolderOpen size={36} className="mx-auto opacity-40" />
          <h3 className="vb-heading text-lg mt-3 mb-1">No folders yet</h3>
          <p className="text-sm opacity-70 mb-3">
            Start by creating the three standard folders — Manual,
            Procedures, Forms — or roll your own.
          </p>
          {canWrite && (
            <div className="flex justify-center gap-2 flex-wrap">
              <button className="vb-btn" onClick={seedDefaults} disabled={busy}
                data-testid="cd-seed-defaults">
                <FolderPlus size={14} /> Create standard folders
              </button>
              <button className="vb-btn ghost" onClick={() => openCreateFolder(null)}
                data-testid="cd-add-root-folder">
                <Plus size={14} /> New folder
              </button>
            </div>
          )}
        </div>
      )}

      {folders.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-3">
          {/* Folder tree */}
          <div className="vb-card p-3" data-testid="cd-tree">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase font-semibold opacity-70 flex items-center gap-1">
                <ListTree size={12} /> Folders
              </div>
              {canWrite && (
                <button className="vb-btn ghost sm" onClick={() => openCreateFolder(null)}
                  title="New top-level folder"
                  data-testid="cd-add-root">
                  <FolderPlus size={12} />
                </button>
              )}
            </div>
            <ul className="list-none pl-0 m-0 space-y-0.5">
              <li>
                <div className={`flex items-center gap-1 py-1 px-1.5 rounded cursor-pointer text-sm ${
                    !activeFolderId ? "bg-[var(--vb-elev)] font-semibold" : "hover:bg-[var(--vb-elev)]"}`}
                  onClick={() => setActiveFolderId(null)}
                  data-testid="cd-folder-all">
                  <Grid3x3 size={14} /> <span className="flex-1">All documents (master list)</span>
                </div>
              </li>
              {tree.map((n) => (
                <FolderNode key={n.id} node={n} depth={0}
                  activeId={activeFolderId} expanded={expanded}
                  onToggle={toggleFolder} onSelect={setActiveFolderId}
                  onAddChild={openCreateFolder} onRename={openRenameFolder}
                  onDelete={deleteFolder} canWrite={canWrite} />
              ))}
            </ul>
          </div>

          {/* Document list */}
          <div>
            {/* Toolbar */}
            <div className="vb-card p-3 mb-3">
              {breadcrumb.length > 0 && (
                <div className="text-xs opacity-70 mb-2" data-testid="cd-breadcrumb">
                  {breadcrumb.map((f, i) => (
                    <span key={f.id}>
                      {i > 0 && " › "}
                      <button className="hover:underline"
                        onClick={() => setActiveFolderId(f.id)}>{f.name}</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 opacity-50" />
                  <input className="vb-input pl-8" placeholder="Search title, doc no, tag..."
                    value={q} onChange={(e) => setQ(e.target.value)}
                    data-testid="cd-search" />
                </div>
                <select className="vb-input" value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="cd-status-filter">
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="under_review">Under review</option>
                </select>
                <button className={`vb-btn ghost sm ${showObsolete ? "text-red-500" : ""}`}
                  onClick={() => setShowObsolete((v) => !v)}
                  title={showObsolete ? "Hide obsolete" : "Show obsolete"}
                  data-testid="cd-toggle-obsolete">
                  {showObsolete ? <Eye size={13} /> : <EyeOff size={13} />}
                  {showObsolete ? " Showing obsolete" : " Show obsolete"}
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="vb-card overflow-x-auto" data-testid="cd-doc-list">
              <table className="vb-table text-sm">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Title</th>
                    <th>Doc No.</th>
                    <th style={{ width: 60 }}>Rev</th>
                    <th style={{ width: 110 }}>Effective</th>
                    <th style={{ width: 100 }}>Type</th>
                    <th style={{ width: 100 }}>Status</th>
                    <th style={{ width: 130 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.length === 0 && (
                    <tr><td colSpan={8} className="text-center opacity-60 py-6"
                      data-testid="cd-empty-list">
                      No documents match. Try clearing filters or upload one.
                    </td></tr>
                  )}
                  {docs.map((d, i) => {
                    const hasHistory = !!d.previous_doc_id;
                    const expanded = expandedDocs[d.id];
                    return (
                    <Fragment key={d.id}>
                    <tr
                      data-testid={`cd-doc-row-${d.id}`}
                      style={d.status === "obsolete"
                        ? { opacity: 0.55, textDecoration: "line-through" }
                        : undefined}>
                      <td className="opacity-60">
                        <div className="flex items-center gap-1">
                          {hasHistory && (
                            <button onClick={() => toggleHistory(d)}
                              className="opacity-70 hover:opacity-100"
                              style={{ padding: "1px 2px" }}
                              title="Show previous revisions"
                              data-testid={`cd-doc-expand-${d.id}`}>
                              {expanded ? <ChevronDown size={13} />
                                : <ChevronRight size={13} />}
                            </button>
                          )}
                          <span>{i + 1}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1.5">
                          <FileText size={13} className="opacity-60" />
                          <span className="font-medium">{d.title || "—"}</span>
                        </div>
                        {d.parent_doc_id && (
                          <div className="text-[10px] opacity-60 mt-0.5 flex items-center gap-1">
                            <Link2 size={9} /> linked to parent
                          </div>
                        )}
                      </td>
                      <td className="font-mono text-[11px]">{d.doc_no || "—"}</td>
                      <td>{d.revision || "—"}</td>
                      <td className="text-xs">{fmt(d.effective_date)}</td>
                      <td className="text-xs opacity-70">{
                        (DOC_TYPES.find(([k]) => k === d.doc_type)?.[1]) || d.doc_type
                      }</td>
                      <td>
                        <span className={`vb-pill text-[10px] ${
                          d.status === "active" ? "ok"
                          : d.status === "under_review" ? "warn"
                          : "err"}`}>
                          {STATUS_LABEL[d.status] || d.status}
                        </span>
                      </td>
                      <td>
                        <div className="flex justify-end">
                          <DocRowMenu doc={d} canWrite={canWrite} isOwner={isOwner}
                            onDownload={() => downloadDoc(d)}
                            onRevise={() => setRevisingDoc(d)}
                            onEdit={() => setEditingDoc(d)}
                            onObsolete={() => obsoleteDoc(d)}
                            onDelete={() => hardDeleteDoc(d)} />
                        </div>
                      </td>
                    </tr>
                    {expanded && expanded !== "loading" && (
                      <tr data-testid={`cd-doc-history-${d.id}`}>
                        <td colSpan={8} style={{ paddingLeft: 32, background: "var(--vb-elev)" }}>
                          <div className="text-[10px] uppercase font-semibold opacity-70 mb-1 flex items-center gap-1">
                            <History size={10} /> Previous revisions ({expanded.length})
                          </div>
                          {expanded.length === 0 ? (
                            <div className="text-xs opacity-60 py-2">No prior revisions on record.</div>
                          ) : (
                            <table className="vb-table text-xs" style={{ marginBottom: 4 }}>
                              <thead>
                                <tr>
                                  <th>Doc No.</th><th>Rev</th><th>Effective</th>
                                  <th>Status</th><th>Obsoleted</th><th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {expanded.map((p) => (
                                  <tr key={p.id}
                                    data-testid={`cd-doc-history-row-${p.id}`}
                                    style={{ opacity: 0.75 }}>
                                    <td className="font-mono text-[10px]">{p.doc_no}</td>
                                    <td>{p.revision}</td>
                                    <td>{fmt(p.effective_date)}</td>
                                    <td>
                                      <span className="vb-pill err text-[10px]">Obsolete</span>
                                    </td>
                                    <td className="text-[10px] opacity-70">
                                      {p.obsoleted_by_name || "—"} · {fmt(p.obsoleted_at)}
                                    </td>
                                    <td className="text-right">
                                      <button className="vb-btn ghost" style={{ padding: "2px 5px" }}
                                        onClick={() => downloadDoc(p)}
                                        title="Download this revision"
                                        data-testid={`cd-doc-history-download-${p.id}`}>
                                        <Download size={11} />
                                      </button>
                                      {isOwner && (
                                        <button className="vb-btn ghost" style={{ padding: "2px 5px", marginLeft: 4 }}
                                          onClick={() => hardDeleteDoc(p)}
                                          title="Owner-only: permanently delete this obsolete revision"
                                          data-testid={`cd-doc-history-delete-${p.id}`}>
                                          <AlertOctagon size={11} />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                    {expanded === "loading" && (
                      <tr>
                        <td colSpan={8} className="text-center text-xs opacity-60 py-2">
                          Loading history…
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showUpload && (
        <UploadModal folders={folders} defaultFolderId={activeFolderId}
          onClose={() => setShowUpload(false)}
          onSaved={() => { setShowUpload(false); loadDocs(); loadSummary(); loadFolders(); }} />
      )}
      {editingDoc && (
        <EditDocModal doc={editingDoc} folders={folders}
          onClose={() => setEditingDoc(null)}
          onSaved={() => { setEditingDoc(null); loadDocs(); loadFolders(); }} />
      )}
      {revisingDoc && (
        <RevisionRequestModal doc={revisingDoc}
          onClose={() => setRevisingDoc(null)}
          onSaved={() => { setRevisingDoc(null); loadDocs(); loadSummary(); loadRevisions(); }} />
      )}
      {showQueue && (
        <RevisionQueueModal requests={pendingRevs} canApprove={canWrite}
          onRefresh={() => Promise.all([loadRevisions(), loadDocs(), loadSummary()])}
          onClose={() => setShowQueue(false)} />
      )}
      {folderPrompt && (
        <FolderPromptModal prompt={folderPrompt}
          onClose={() => setFolderPrompt(null)} />
      )}
    </div>
  );
}

/* ============ UPLOAD MODAL ============ */
function UploadModal({ folders, defaultFolderId, onClose, onSaved }) {
  const [folderId, setFolderId] = useState(defaultFolderId || folders[0]?.id || "");
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [docNo, setDocNo] = useState("");
  const [revision, setRevision] = useState("00");
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [docType, setDocType] = useState("procedure");
  const [status, setStatus] = useState("active");
  const [autofilled, setAutofilled] = useState(false);
  // Track which fields the user has manually edited so we don't overwrite
  // their intent when the file changes; untouched fields always refresh
  // from the latest filename parse.
  const [touched, setTouched] = useState({});
  const markTouched = (k) => setTouched((t) => t[k] ? t : { ...t, [k]: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [linkPrompt, setLinkPrompt] = useState(null);

  const pickFile = (f) => {
    setFile(f || null);
    if (!f) return;
    const parsed = parseComplianceFilename(f.name);
    // Refresh every field the user hasn't manually touched.
    if (parsed.title && !touched.title) setTitle(parsed.title);
    if (parsed.doc_no && !touched.doc_no) setDocNo(parsed.doc_no);
    if (!touched.revision) setRevision(parsed.revision || "00");
    if (parsed.doc_type && !touched.doc_type) setDocType(parsed.doc_type);
    // Match a sensible folder to the doc_type if user hasn't picked one
    if (parsed.doc_type && folders.length > 0 && !touched.folder && !defaultFolderId) {
      const lookup = {
        manual: /manual/i, procedure: /procedure/i, prp: /procedure|prp/i,
        wi: /procedure|work|instruction/i, sop: /procedure|sop/i, form: /form/i,
      }[parsed.doc_type];
      if (lookup) {
        const match = folders.find((x) => lookup.test(x.name));
        if (match) setFolderId(match.id);
      }
    }
    setAutofilled(Boolean(parsed.title || parsed.doc_no || parsed.doc_type));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Pick a file to upload."); return; }
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder_id", folderId);
      fd.append("title", title);
      fd.append("doc_no", docNo);
      fd.append("revision", revision);
      fd.append("effective_date", effectiveDate);
      fd.append("doc_type", docType);
      fd.append("status", status);
      const { data } = await api.post("/compliance-docs/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.suggested_parent) {
        setLinkPrompt({ docId: data.document.id, suggested: data.suggested_parent });
      } else {
        toast.success("Document uploaded");
        onSaved();
      }
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const confirmLink = async () => {
    try {
      await api.put(`/compliance-docs/documents/${linkPrompt.docId}`,
        { parent_doc_id: linkPrompt.suggested.id });
      toast.success("Linked to parent document");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Link failed");
    }
  };

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        {!linkPrompt ? (
          <form className="vb-modal p-5 w-full max-w-2xl"
            style={{ maxHeight: "92vh", overflowY: "auto" }}
            data-testid="cd-upload-modal"
            onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="vb-heading text-xl">Upload compliance document</h3>
              <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
                <X size={18} />
              </button>
            </div>
            <div className="mb-3">
              <Field label="File (PDF, DOCX, XLSX, PPTX, TXT, JPG, PNG · max 20 MB)" required
                hint="Tip: name your file like SPG-P-062-(F01)-00 Title.pdf — we'll auto-fill the fields below.">
                <input className="vb-input" type="file" required
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.jpg,.jpeg,.png"
                  onChange={(e) => pickFile(e.target.files?.[0] || null)}
                  data-testid="cd-up-file" />
              </Field>
              {autofilled && (
                <div className="vb-pill ok text-[10px] mt-2"
                  data-testid="cd-up-autofilled-hint">
                  Auto-filled from filename — please review & edit if needed
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Folder" required>
                <select className="vb-input" value={folderId} required
                  onChange={(e) => { setFolderId(e.target.value); markTouched("folder"); }}
                  data-testid="cd-up-folder">
                  <option value="" disabled>Select a folder…</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {folderPath(f, folders)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Document type">
                <select className="vb-input" value={docType}
                  onChange={(e) => { setDocType(e.target.value); markTouched("doc_type"); }}
                  data-testid="cd-up-type">
                  {DOC_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Document title" required>
                <input className="vb-input" required value={title}
                  placeholder="e.g. Planning to achieve FSMS Objectives"
                  onChange={(e) => { setTitle(e.target.value); markTouched("title"); }}
                  data-testid="cd-up-title" />
              </Field>
              <Field label="Document number"
                hint="e.g. JIFP-P-062 or JIFP-P-062-(F01) for a form">
                <input className="vb-input" value={docNo}
                  onChange={(e) => { setDocNo(e.target.value); markTouched("doc_no"); }}
                  data-testid="cd-up-doc-no" />
              </Field>
              <Field label="Revision">
                <input className="vb-input" value={revision}
                  onChange={(e) => { setRevision(e.target.value); markTouched("revision"); }}
                  placeholder="00"
                  data-testid="cd-up-rev" />
              </Field>
              <Field label="Effective date">
                <input className="vb-input" type="date" value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  data-testid="cd-up-eff" />
              </Field>
              <Field label="Status">
                <select className="vb-input" value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  data-testid="cd-up-status">
                  <option value="active">Active</option>
                  <option value="under_review">Under review (draft)</option>
                </select>
              </Field>
            </div>
            {err && <div className="vb-pill err mt-3 text-[11px]"
              data-testid="cd-up-err">{err}</div>}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="vb-btn" disabled={busy}
                data-testid="cd-up-submit">
                {busy ? "Uploading…" : "Upload"}
              </button>
            </div>
          </form>
        ) : (
          <div className="vb-modal p-5 w-full max-w-lg"
            data-testid="cd-link-prompt"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="vb-heading text-lg mb-2 flex items-center gap-2">
              <Link2 size={18} /> Link to parent procedure?
            </h3>
            <p className="text-sm opacity-80 mb-3">
              This form&apos;s document number matches an existing procedure:
            </p>
            <div className="p-3 rounded-lg mb-3"
              style={{ background: "var(--vb-elev)", border: "1px solid var(--vb-border)" }}>
              <div className="font-mono text-xs opacity-70">
                {linkPrompt.suggested.doc_no}
              </div>
              <div className="font-semibold">
                {linkPrompt.suggested.title}
              </div>
            </div>
            <p className="text-xs opacity-70 mb-3">
              Linking makes the parent–form relationship visible in the
              master list and detail views. You can change or remove the
              link later from the Edit dialog.
            </p>
            <div className="flex justify-end gap-2">
              <button className="vb-btn ghost" onClick={() => {
                toast.success("Uploaded without link");
                onSaved();
              }} data-testid="cd-link-skip">
                Skip
              </button>
              <button className="vb-btn" onClick={confirmLink}
                data-testid="cd-link-confirm">
                <Link2 size={14} /> Link them
              </button>
            </div>
          </div>
        )}
      </div>
    </Portal>
  );
}

/* ============ EDIT MODAL ============ */
function EditDocModal({ doc, folders, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: doc.title || "",
    doc_no: doc.doc_no || "",
    revision: doc.revision || "00",
    effective_date: doc.effective_date || "",
    doc_type: doc.doc_type || "other",
    status: doc.status || "active",
    folder_id: doc.folder_id,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.put(`/compliance-docs/documents/${doc.id}`, { ...form });
      toast.success("Updated");
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        <form className="vb-modal p-5 w-full max-w-2xl"
          style={{ maxHeight: "92vh", overflowY: "auto" }}
          data-testid="cd-edit-modal"
          onClick={(e) => e.stopPropagation()} onSubmit={submit}>
          <div className="flex items-start justify-between mb-3">
            <h3 className="vb-heading text-xl">Edit document metadata</h3>
            <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Folder">
              <select className="vb-input" value={form.folder_id}
                onChange={(e) => setForm({ ...form, folder_id: e.target.value })}
                data-testid="cd-ed-folder">
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>{folderPath(f, folders)}</option>
                ))}
              </select>
            </Field>
            <Field label="Document type">
              <select className="vb-input" value={form.doc_type}
                onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
                data-testid="cd-ed-type">
                {DOC_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Title">
              <input className="vb-input" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="cd-ed-title" />
            </Field>
            <Field label="Document number">
              <input className="vb-input" value={form.doc_no}
                onChange={(e) => setForm({ ...form, doc_no: e.target.value })}
                data-testid="cd-ed-doc-no" />
            </Field>
            <Field label="Revision">
              <input className="vb-input" value={form.revision}
                onChange={(e) => setForm({ ...form, revision: e.target.value })}
                data-testid="cd-ed-rev" />
            </Field>
            <Field label="Effective date">
              <input className="vb-input" type="date" value={form.effective_date || ""}
                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                data-testid="cd-ed-eff" />
            </Field>
            <Field label="Status">
              <select className="vb-input" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="cd-ed-status">
                <option value="active">Active</option>
                <option value="under_review">Under review</option>
                <option value="obsolete">Obsolete</option>
              </select>
            </Field>
          </div>
          {err && <div className="vb-pill err mt-3 text-[11px]">{err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy}
              data-testid="cd-ed-submit">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </Portal>
  );
}

/* ============ SHARED ============ */
function Field({ label, children, hint, required }) {
  return (
    <div>
      <label className="text-xs uppercase font-semibold opacity-70">
        {label}{required && <span style={{ color: "#dc2626" }}> *</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] opacity-60 mt-0.5">{hint}</p>}
    </div>
  );
}

function folderPath(f, all) {
  const chain = [f.name];
  let cur = f;
  const byId = {}; all.forEach((x) => { byId[x.id] = x; });
  while (cur.parent_id) {
    cur = byId[cur.parent_id];
    if (!cur) break;
    chain.unshift(cur.name);
  }
  return chain.join(" › ");
}

/* ============ FOLDER PROMPT MODAL (in-app replacement for window.prompt) ============ */
function FolderPromptModal({ prompt, onClose }) {
  const [name, setName] = useState(prompt.initialName || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isCreate = prompt.mode === "create";

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr("Please enter a name."); return; }
    setBusy(true); setErr("");
    try {
      await prompt.onSave(name.trim());
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        <form className="vb-modal p-5 w-full max-w-md"
          data-testid="cd-folder-prompt"
          onClick={(e) => e.stopPropagation()} onSubmit={submit}>
          <div className="flex items-start justify-between mb-3">
            <h3 className="vb-heading text-lg">
              {isCreate ? "New folder" : "Rename folder"}
            </h3>
            <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
              <X size={18} />
            </button>
          </div>
          <Field label="Folder name" required hint={isCreate
            ? "e.g. PRP, WI/SOP, Records"
            : "Enter the new name."}>
            <input className="vb-input" required autoFocus value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="cd-folder-prompt-input" />
          </Field>
          {err && <div className="vb-pill err mt-3 text-[11px]">{err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy}
              data-testid="cd-folder-prompt-submit">
              {busy ? "Saving…" : isCreate ? "Create" : "Rename"}
            </button>
          </div>
        </form>
      </div>
    </Portal>
  );
}

/* ============ REVISION REQUEST MODAL ============ */
function RevisionRequestModal({ doc, onClose, onSaved }) {
  const bumpRev = (r) => {
    const n = parseInt(r || "0", 10);
    if (Number.isNaN(n)) return `${r || "00"}.1`;
    return String(n + 1).padStart(2, "0");
  };
  const [dcn, setDcn] = useState("");
  const [reason, setReason] = useState("");
  const [proposedChanges, setProposedChanges] = useState("");
  const [newRevision, setNewRevision] = useState(bumpRev(doc.revision));
  const [newEffectiveDate, setNewEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      fd.append("dcn", dcn);
      fd.append("reason", reason);
      fd.append("proposed_changes", proposedChanges);
      fd.append("new_revision", newRevision);
      fd.append("new_effective_date", newEffectiveDate);
      await api.post(`/compliance-docs/documents/${doc.id}/revision-requests`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Revision request submitted — awaiting approval");
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        <form className="vb-modal p-5 w-full max-w-2xl"
          style={{ maxHeight: "92vh", overflowY: "auto" }}
          data-testid="cd-rev-modal"
          onClick={(e) => e.stopPropagation()} onSubmit={submit}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="vb-heading text-xl flex items-center gap-2">
                <GitBranch size={18} /> Request revision
              </h3>
              <p className="text-xs opacity-70 mt-0.5">
                {doc.doc_no} · Rev {doc.revision} — {doc.title}
              </p>
            </div>
            <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Doc Change Notice (DCN)" required
              hint="Reference number for this change request (e.g. DCN-2026-014)">
              <input className="vb-input" required value={dcn}
                onChange={(e) => setDcn(e.target.value)}
                data-testid="cd-rev-dcn" />
            </Field>
            <Field label="New revision" required>
              <input className="vb-input" required value={newRevision}
                onChange={(e) => setNewRevision(e.target.value)}
                data-testid="cd-rev-new-rev" />
            </Field>
            <Field label="Effective date (once approved)" required>
              <input className="vb-input" type="date" required value={newEffectiveDate}
                onChange={(e) => setNewEffectiveDate(e.target.value)}
                data-testid="cd-rev-eff" />
            </Field>
            <Field label="Updated file (optional now, required to approve)"
              hint="PDF/DOCX/XLSX/PPTX/TXT/JPG/PNG · max 20 MB">
              <input className="vb-input" type="file"
                accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="cd-rev-file" />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Reason for changes" required>
              <textarea className="vb-input" required rows={2} value={reason}
                placeholder="e.g. Aligning with updated ISO 22000 clause 7.2"
                onChange={(e) => setReason(e.target.value)}
                data-testid="cd-rev-reason" />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Details of proposed changes" required>
              <textarea className="vb-input" required rows={4} value={proposedChanges}
                placeholder="Describe what is changing — sections, wording, numbering, attachments, etc."
                onChange={(e) => setProposedChanges(e.target.value)}
                data-testid="cd-rev-changes" />
            </Field>
          </div>
          <div className="mt-3 p-3 rounded-lg text-[11px] opacity-80"
            style={{ background: "var(--vb-elev)", border: "1px solid var(--vb-border)" }}>
            <strong>What happens next:</strong> your request goes to the
            revision queue. An owner or manager will approve, send back for
            amendment, or reject. On approval, the current version is
            marked <em>obsolete</em> (kept for audit) and the new version
            takes over as the active revision.
          </div>
          {err && <div className="vb-pill err mt-3 text-[11px]"
            data-testid="cd-rev-err">{err}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy}
              data-testid="cd-rev-submit">
              {busy ? "Submitting…" : "Submit for approval"}
            </button>
          </div>
        </form>
      </div>
    </Portal>
  );
}

/* ============ REVISION QUEUE MODAL ============ */
function RevisionQueueModal({ requests, canApprove, onRefresh, onClose }) {
  const [statusFilter, setStatusFilter] = useState("open"); // open | approved | rejected | all
  const [selected, setSelected] = useState(null);

  const filtered = requests.filter((r) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "open")
      return r.status === "pending" || r.status === "needs_amendment";
    return r.status === statusFilter;
  });

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        <div className="vb-modal p-5 w-full max-w-4xl"
          style={{ maxHeight: "92vh", overflowY: "auto" }}
          data-testid="cd-queue-modal"
          onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="vb-heading text-xl flex items-center gap-2">
                <ClipboardList size={18} /> Revision queue
              </h3>
              <p className="text-xs opacity-70 mt-0.5">
                Track and review pending, returned and closed revision requests.
              </p>
            </div>
            <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
              <X size={18} />
            </button>
          </div>

          {!selected ? (
            <>
              <div className="flex gap-2 mb-3 flex-wrap">
                {["open", "approved", "rejected", "all"].map((k) => (
                  <button key={k}
                    className={`vb-pill ${statusFilter === k ? "ok" : ""}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setStatusFilter(k)}
                    data-testid={`cd-queue-filter-${k}`}>
                    {k === "open" ? "Open (pending + needs amendment)"
                      : k[0].toUpperCase() + k.slice(1)}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="vb-table text-sm">
                  <thead>
                    <tr>
                      <th>DCN</th>
                      <th>Document</th>
                      <th>Current → New</th>
                      <th>Requester</th>
                      <th>Requested</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={7} className="text-center opacity-60 py-6"
                        data-testid="cd-queue-empty">
                        No requests in this filter.
                      </td></tr>
                    )}
                    {filtered.map((r) => (
                      <tr key={r.id}
                        data-testid={`cd-queue-row-${r.id}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelected(r)}>
                        <td className="font-mono text-xs">{r.dcn}</td>
                        <td>
                          <div className="font-medium">{r.doc_title}</div>
                          <div className="text-[10px] opacity-60 font-mono">{r.doc_no}</div>
                        </td>
                        <td className="text-xs">Rev {r.current_revision} → {r.new_revision}</td>
                        <td className="text-xs">{r.requester_name}</td>
                        <td className="text-xs opacity-70">{fmt(r.requested_at)}</td>
                        <td>
                          <span className={`vb-pill text-[10px] ${
                            r.status === "approved" ? "ok"
                            : r.status === "pending" ? "warn"
                            : r.status === "needs_amendment" ? "warn"
                            : "err"}`}>
                            {r.status === "needs_amendment" ? "Needs amendment"
                              : r.status[0].toUpperCase() + r.status.slice(1)}
                          </span>
                        </td>
                        <td><ChevronRight size={13} className="opacity-50" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <RevisionDetail req={selected} canApprove={canApprove}
              onBack={() => setSelected(null)}
              onRefresh={async () => { await onRefresh(); setSelected(null); }} />
          )}
        </div>
      </div>
    </Portal>
  );
}

function RevisionDetail({ req, canApprove, onBack, onRefresh }) {
  const [notes, setNotes] = useState("");
  const [action, setAction] = useState(null); // approve | return | reject
  const [busy, setBusy] = useState(false);
  const canAct = canApprove && req.status === "pending";
  const hasFile = !!req.file_path;

  const commit = async () => {
    setBusy(true);
    try {
      await api.put(`/compliance-docs/revision-requests/${req.id}/${action}`,
        { notes: notes.trim() });
      toast.success(action === "approve" ? "Approved — new revision live"
        : action === "return" ? "Sent back for amendment"
        : "Rejected");
      await onRefresh();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/compliance-docs/revision-requests/${req.id}/download`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = req.file_name || "revision";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div data-testid="cd-queue-detail">
      <button className="vb-btn ghost sm mb-3" onClick={onBack}>
        ← Back to queue
      </button>
      <div className="vb-card p-3 mb-3">
        <div className="text-xs uppercase opacity-70 mb-1">DCN {req.dcn}</div>
        <div className="font-semibold">{req.doc_title}</div>
        <div className="text-xs opacity-60 font-mono">
          {req.doc_no} · Rev {req.current_revision} → Rev {req.new_revision}
        </div>
        <div className="text-xs mt-2 opacity-70">
          Requested by <strong>{req.requester_name}</strong> on {fmt(req.requested_at)}
        </div>
      </div>
      <Field label="Reason for changes">
        <div className="vb-input" style={{ background: "var(--vb-elev)",
          minHeight: "auto", whiteSpace: "pre-wrap" }}>{req.reason || "—"}</div>
      </Field>
      <div className="mt-3">
        <Field label="Details of proposed changes">
          <div className="vb-input" style={{ background: "var(--vb-elev)",
            minHeight: "auto", whiteSpace: "pre-wrap" }}>{req.proposed_changes || "—"}</div>
        </Field>
      </div>
      <div className="mt-3">
        <Field label="Attached updated file">
          <div>
            {hasFile ? (
              <button className="vb-btn ghost sm" type="button" onClick={download}
                data-testid="cd-queue-download">
                <Download size={13} /> {req.file_name}
              </button>
            ) : (
              <span className="text-xs opacity-60">
                No file yet — requester must attach the updated version before approval.
              </span>
            )}
          </div>
        </Field>
      </div>

      {req.history?.length > 0 && (
        <div className="mt-3">
          <div className="text-xs uppercase opacity-70 mb-1 flex items-center gap-1">
            <History size={12} /> History
          </div>
          <ul className="list-none p-0 m-0 space-y-1">
            {req.history.map((h, i) => (
              <li key={i} className="text-xs opacity-80" data-testid={`cd-queue-history-${i}`}>
                <strong>{h.action}</strong> · {h.actor_name || h.actor} · {fmt(h.at)}
                {h.notes && <> — {h.notes}</>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {req.reviewer_notes && (
        <div className="mt-3 p-3 rounded-lg" style={{
          background: req.status === "approved" ? "rgba(34,197,94,0.06)"
            : "rgba(220,38,38,0.06)",
          border: `1px solid ${req.status === "approved"
            ? "rgba(34,197,94,0.3)" : "rgba(220,38,38,0.3)"}`,
        }}>
          <div className="text-xs uppercase font-semibold mb-1">Reviewer notes</div>
          <div className="text-sm">{req.reviewer_notes}</div>
          <div className="text-[10px] opacity-70 mt-1">
            by {req.reviewer_name} · {fmt(req.reviewed_at)}
          </div>
        </div>
      )}

      {canAct && (
        <div className="mt-4 p-3 rounded-lg" style={{
          background: "var(--vb-elev)", border: "1px solid var(--vb-border)" }}>
          <div className="text-xs uppercase font-semibold mb-2">Take action</div>
          <div className="flex gap-2 mb-2 flex-wrap">
            <button type="button"
              className={`vb-btn ${action === "approve" ? "" : "ghost"}`}
              onClick={() => setAction("approve")}
              disabled={!hasFile}
              title={!hasFile ? "File must be attached before approval" : undefined}
              data-testid="cd-queue-action-approve">
              <CheckCircle2 size={13} /> Approve
            </button>
            <button type="button"
              className={`vb-btn ${action === "return" ? "warn" : "ghost"}`}
              onClick={() => setAction("return")}
              data-testid="cd-queue-action-return">
              <RotateCcw size={13} /> Send back
            </button>
            <button type="button"
              className={`vb-btn ${action === "reject" ? "danger" : "ghost"}`}
              onClick={() => setAction("reject")}
              data-testid="cd-queue-action-reject">
              <XCircle size={13} /> Reject
            </button>
          </div>
          {action && (
            <>
              <textarea className="vb-input" rows={2} value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={action === "approve"
                  ? "Optional approval note"
                  : action === "return"
                    ? "What needs to be amended? (required)"
                    : "Reason for rejection (required)"}
                data-testid="cd-queue-action-notes" />
              <div className="flex justify-end gap-2 mt-2">
                <button className="vb-btn ghost sm" onClick={() => { setAction(null); setNotes(""); }}>
                  Cancel
                </button>
                <button className="vb-btn sm" onClick={commit}
                  disabled={busy || (action !== "approve" && !notes.trim())}
                  title={(action !== "approve" && !notes.trim())
                    ? "Please provide notes before confirming"
                    : undefined}
                  data-testid="cd-queue-action-confirm">
                  {busy ? "Working…" : `Confirm ${action}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


/* ============ DOC ROW KEBAB MENU ============ */
function DocRowMenu({ doc, canWrite, isOwner, onDownload, onRevise, onEdit,
                      onObsolete, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const closeMouse = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const closeKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", closeMouse);
    document.addEventListener("keydown", closeKey);
    return () => {
      document.removeEventListener("mousedown", closeMouse);
      document.removeEventListener("keydown", closeKey);
    };
  }, [open]);

  const items = [];
  items.push({
    key: "download", label: "Download", icon: Download,
    onClick: onDownload, testid: `cd-doc-download-${doc.id}`,
  });
  if (canWrite) {
    if (doc.status === "active") {
      items.push({
        key: "revise", label: "Request revision", icon: GitBranch,
        onClick: onRevise, testid: `cd-doc-revise-${doc.id}`,
      });
    }
    items.push({
      key: "edit", label: "Edit metadata", icon: Edit3,
      onClick: onEdit, testid: `cd-doc-edit-${doc.id}`,
    });
    if (doc.status !== "obsolete") {
      items.push({
        key: "obsolete", label: "Mark obsolete", icon: Trash2,
        onClick: onObsolete, testid: `cd-doc-obsolete-${doc.id}`,
        danger: false,
      });
    }
    if (isOwner) {
      items.push({
        key: "delete", label: "Delete permanently", icon: AlertOctagon,
        onClick: onDelete, testid: `cd-doc-delete-${doc.id}`,
        danger: true,
      });
    }
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className="vb-btn ghost" style={{ padding: "3px 6px" }}
        onClick={() => setOpen((v) => !v)} title="Actions"
        data-testid={`cd-doc-menu-${doc.id}`}>
        <MoreVertical size={13} />
      </button>
      {open && (
        <div className="vb-card"
          style={{
            position: "absolute", right: 0, top: "calc(100% + 4px)",
            zIndex: 30, minWidth: 190, padding: 4,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}
          data-testid={`cd-doc-menu-panel-${doc.id}`}>
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <button key={it.key}
                className="w-full text-left px-3 py-1.5 text-sm rounded flex items-center gap-2 hover:bg-[var(--vb-elev)]"
                style={it.danger ? { color: "#dc2626" } : undefined}
                onClick={() => { setOpen(false); it.onClick(); }}
                data-testid={it.testid}>
                <Icon size={13} /> {it.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

